import { ArtefactScopeSwitchForm } from '@modules/common/components/ArtefactScopeSwitchForm';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useUpdateDashboard } from '@modules/dashboard/hooks/useUpdateDashboard';
import { EditableDashboard } from '@modules/dashboard/types';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ArtefactType, Scope } from 'generated/graphql';
import { useState } from 'react';

interface ChangeDashboardDiscoverabilityDialogContentProps {
  dashboard: EditableDashboard;
}

export const ChangeDashboardDiscoverabilityDialogContent = ({
  dashboard,
}: ChangeDashboardDiscoverabilityDialogContentProps) => {
  const { showSuccessNotification } = useNotificationContext();

  const [scope, setScope] = useState(dashboard.scope);

  const { updateDashboard, loading } = useUpdateDashboard();

  const onChange = (checked: boolean) => {
    setScope(checked ? Scope.Public : Scope.Private);
  };

  const { closeModal } = useMuiModalContext();

  const onClose = () => closeModal();

  const updateDiscoverability = async () => {
    await updateDashboard({
      updateRequest: {
        id: dashboard.id,
        viewToken: dashboard.viewToken,
        editToken: dashboard.editToken,
        scope,
      },
    });

    showSuccessNotification({
      contentComponent: 'Dashboard discoverability updated successfully.',
      props: {
        isClosable: true,
      },
    });
    onClose();
    trackDashboardEvent({
      eventName: 'Dashboard Details Updated',
      dashboardLink: window.location.href,
      updates: {
        discoverability:
          scope === Scope.Public ? 'Discoverable' : 'Not Discoverable',
      },
    });
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">
            Change Discoverability of "{dashboard.title}"
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <ArtefactScopeSwitchForm
          scope={scope}
          onScopeChange={onChange}
          artefactType={ArtefactType.Dashboard}
          analysisUseCase={undefined}
          isDisabled={loading}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction="row" m={1} spacing={2}>
          <Button
            onClick={onClose}
            variant="outlined"
            endIcon={<EscHotKeyIcon />}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            onClick={updateDiscoverability}
            variant="contained"
            autoFocus
          >
            Update
          </LoadingButton>
        </Stack>
      </DialogActions>
    </>
  );
};
